import { useEffect, useRef, useState } from 'react';
import { useApi } from '../lib/api';

interface Hit {
    kind: 'community' | 'post' | 'tag';
    id: string;
    title: string;
    snippet: string;
    url: string;
}
interface Results {
    query: string;
    users:       Hit[] | null;
    communities: Hit[] | null;
    posts:       Hit[] | null;
    tags:        Hit[] | null;
    total: number;
}

/** Debounced global search — communities/posts/tags. `/` shortcut focuses it. */
export function SearchBar() {
    const api = useApi();
    const [q,    setQ]    = useState('');
    const [open, setOpen] = useState(false);
    const [res,  setRes]  = useState<Results | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const wrapRef  = useRef<HTMLDivElement>(null);
    const lastQ    = useRef('');

    // '/' shortcut, ignored while typing.
    useEffect(() => {
        function onKey(e: KeyboardEvent) {
            if (e.key !== '/') return;
            const t = e.target as HTMLElement | null;
            if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
            e.preventDefault();
            inputRef.current?.focus();
        }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    // Close on outside click.
    useEffect(() => {
        function onClick(e: MouseEvent) {
            if (!open) return;
            if (wrapRef.current?.contains(e.target as Node)) return;
            setOpen(false);
        }
        document.addEventListener('click', onClick);
        return () => document.removeEventListener('click', onClick);
    }, [open]);

    // Debounced fetch.
    useEffect(() => {
        if (q.trim().length < 2) { setRes(null); setOpen(false); return; }
        const term = q.trim();
        lastQ.current = term;
        const t = setTimeout(async () => {
            try {
                const r = await api.get<Results>('/api/search?q=' + encodeURIComponent(term));
                if (r.query === lastQ.current) { setRes(r); setOpen(true); }
            } catch { /* silent — user still typing */ }
        }, 250);
        return () => clearTimeout(t);
    }, [q, api]);

    const groups: Array<[string, Hit[]]> = [];
    if (res) {
        if (res.users      ?.length) groups.push(['People',      res.users]);
        if (res.communities?.length) groups.push(['Communities', res.communities]);
        if (res.tags       ?.length) groups.push(['Tags',        res.tags]);
        if (res.posts      ?.length) groups.push(['Posts',       res.posts]);
    }

    return (
        <div className="topbar-search" ref={wrapRef}>
            <input
                ref={inputRef}
                type="search"
                placeholder="Search groups, communities, posts…"
                autoComplete="off"
                aria-label="Search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onFocus={() => { if (res && res.total > 0) setOpen(true); }}
                onKeyDown={(e) => { if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); } }}
            />
            {open && res && (
                <div className="topbar-search-results">
                    {groups.length === 0 ? (
                        <div className="search-empty">No results for "{res.query}"</div>
                    ) : groups.map(([label, hits]) => (
                        <div className="search-group" key={label}>
                            <div className="search-group-head">{label}</div>
                            {hits.map(h => (
                                <a key={h.id} className="search-hit" href={h.url}>
                                    <div className="search-hit-title">{h.title || '(untitled)'}</div>
                                    {h.snippet && <div className="search-hit-snippet">{h.snippet}</div>}
                                </a>
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
